/**
 * Central tuning knobs for the simulation. Pure data — no Phaser.
 *
 * These are the "feel targets" from docs/DESIGN.md §8.5 expressed as numbers.
 * Expect to tweak by playing; tests assert *relationships*, not these literals.
 */
export const SIM = {
  /** Max spin value. Spin doubles as a bey's health/stamina pool. */
  MAX_SPIN: 100,

  /** Idle spin drain: seconds for a bey to empty from full purely by idling. */
  IDLE_EMPTY_SECONDS_AT_MIN_STAMINA: 45, // stamina = 1 (fast drain)
  IDLE_EMPTY_SECONDS_AT_MAX_STAMINA: 110, // stamina = 10 (marathon)

  /** Collision clash tuning. */
  CLASH: {
    /** Base spin lost on a clash, before stat scaling, per unit of impact speed. */
    BASE_SPIN_LOSS: 1.1,
    /** How much the aggressor's spin loss is reduced (0..1). */
    AGGRESSOR_RELIEF: 0.65,
    /** Floor on spin loss so even gentle taps cost a sliver. */
    MIN_SPIN_LOSS: 0.25,
    /** Defense divisor strength: higher = defense matters more. */
    DEFENSE_K: 0.09,
    /** Attack multiplier strength: higher = attack matters more. */
    ATTACK_K: 0.14,
    /** Type-triangle damage multiplier when attacker has the advantage. */
    TYPE_ADVANTAGE: 1.3,
    /** Type-triangle damage multiplier when attacker is disadvantaged. */
    TYPE_DISADVANTAGE: 0.8,
    /** Opposite-spin clash: extra spin the loser bleeds (the "spin-steal"). */
    OPPOSITE_SPIN_LOSS_MULT: 1.6,
    /** Opposite-spin clash: the winner absorbs rotation, so loses less. */
    OPPOSITE_SPIN_WINNER_RELIEF: 0.5,
  },

  /** Burst tuning — rare & dramatic (DESIGN §3.5). */
  BURST: {
    METER_MAX: 100,
    /** Impact speed below this contributes nothing to the burst meter. */
    IMPACT_THRESHOLD: 7.5,
    /** Burst gain per unit of over-threshold impact, before durability. */
    GAIN_K: 9,
    /** Durability divisor: higher durability => less burst gain. */
    DURABILITY_K: 0.12,
    /** Burst meter decay per second when not being hit. */
    DECAY_PER_SEC: 14,
  },

  /** Special-move charge meter (DESIGN §5). */
  SPECIAL: {
    METER_MAX: 100,
    /** Meter gained per point of spin damage dealt to the opponent. */
    GAIN_PER_DEALT: 1.3,
    /** Meter gained per point of spin damage taken. */
    GAIN_PER_TAKEN: 0.7,
    /** Passive meter trickle per second. */
    TRICKLE_PER_SEC: 1.5,
    /** Spin restored by the stamina special (Second Wind). */
    SECOND_WIND_HEAL: 35,
  },

  /** Knockback applied on a clash (scalar impulse magnitude). */
  KNOCKBACK: {
    BASE: 0.9,
    /** Aggressor pushes harder; loser gets shoved more. */
    AGGRESSOR_BONUS: 1.5,
    /** Heavier beys resist knockback: divisor strength. */
    WEIGHT_K: 0.08,
  },
} as const;
